import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { TransitionState } from "../../GameState"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { ButtonContainer, Buttons, ButtonSize } from "../../generic-sprites/Buttons"
import { DebugPanel } from "../../generic-sprites/DebugPanel"
import { GenericAssets, loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Line } from "../../generic-sprites/Shape"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { loadAssets, PongAssets } from "./PongAssets"
import { PongPos } from "./PongPos"
import { PongStore } from "./PongStore"
import { Cig } from "./sprites/Cig"

export class PongState extends Phaser.State {

    static readonly MAX_UNDEFENDED_CIGS = 10
    private static readonly MAX_GAMES = 5
    private static readonly MIN_GAMES = 3

    paddle: Phaser.Sprite
    addictometer: Phaser.Sprite
    diffGroup: Phaser.Group
    gameGroup: Phaser.Group
    cigs: Phaser.Group
    gameOverDestroyGroup: Phaser.Group
    scoresGroup: Phaser.Group

    gameTimer: Phaser.Timer
    cigLoop: Phaser.TimerEvent

    currentDifficult: Difficulty

    abandonedCigLocations: Array<number> = []

    upperLineSlope: number
    lowerLineSlope: number
    midLoc: Loc
    upperLoc: Loc
    lowerLoc: Loc

    smokingSmoke: Phaser.Sprite
    smoke: Phaser.Animation
    vapingVape: Phaser.Sprite
    vape: Phaser.Animation

    explodeOMeter: Phaser.Sprite
    explodeOMeterAnim: Phaser.Animation

    timeRemainingCounter: Phaser.Text
    difficultyText: Phaser.Text

    undefendedCigs = 0
    timeLeft = 60

    allTheButtons: Array<ButtonContainer> = []
    difficultyButtons: Array<ButtonContainer>

    paddleBaseHeight: number

    gamesPlayed = 0

    totallyAddicted: Phaser.Sound
    somewhatAddicted: Phaser.Sound
    notAddicted: Phaser.Sound
    inhale: Phaser.Sound
    notAddictedSounds = new GameOverSounds()
    somewhatAddictedSounds = new GameOverSounds()
    totallyAddictedSounds = new GameOverSounds()

    debugPanel: DebugPanel

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {

        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)
        this.game.physics.startSystem(Phaser.Physics.ARCADE)
        this.game.add.existing(new BackgroundSprite(this.game, PongAssets.BACKGROUND))

        this.timeLeft = 60
        this.undefendedCigs = 0

        this.addictometer =
            this.game.add.sprite(PongPos.addictometerX(), PongPos.addictometerY(), PongAssets.ADDICTOMETER)
        Scale.scaleSprite(this.addictometer, 0.55)

        this.explodeOMeter = this.game.add.sprite(PongPos.explodeX(), PongPos.explodeY(), PongAssets.EXPLODE)
        Scale.scaleSprite(this.explodeOMeter, 1.1)
        this.explodeOMeter.visible = false
        this.explodeOMeterAnim = this.explodeOMeter.animations.add("explode")

        this.totallyAddicted = this.game.add.sound(PongAssets.TOTALLY_GAME_OVER)
        this.somewhatAddicted = this.game.add.sound(PongAssets.SOMEWHAT_GAME_OVER)
        this.notAddicted = this.game.add.sound(GenericAssets.BELL_SOUND)
        this.inhale = this.game.add.sound(PongAssets.INHALE, 0.5)

        this.notAddictedSounds.sounds[0] = this.game.add.sound(PongAssets.NOT_ADDICTED_1)
        this.notAddictedSounds.sounds[1] = this.game.add.sound(PongAssets.NOT_ADDICTED_2)
        this.notAddictedSounds.sounds[2] = this.game.add.sound(PongAssets.NOT_ADDICTED_3)

        this.somewhatAddictedSounds.sounds[0] = this.game.add.sound(PongAssets.SOMEWHAT_ADDICTED_1)
        this.somewhatAddictedSounds.sounds[1] = this.game.add.sound(PongAssets.SOMEWHAT_ADDICTED_2)
        this.somewhatAddictedSounds.sounds[2] = this.game.add.sound(PongAssets.SOMEWHAT_ADDICTED_3)

        this.totallyAddictedSounds.sounds[0] = this.game.add.sound(PongAssets.TOTALLY_ADDICTED_1)
        this.totallyAddictedSounds.sounds[1] = this.game.add.sound(PongAssets.TOTALLY_ADDICTED_2)
        this.totallyAddictedSounds.sounds[2] = this.game.add.sound(PongAssets.TOTALLY_ADDICTED_3)

        this.midLoc = new Loc(Scale.width() * 0.175, Scale.height() * 0.5)
        this.upperLoc = new Loc(Scale.width() * 0.313, 0)
        this.lowerLoc = new Loc(Scale.width() * 0.313, Scale.height())

        this.upperLineSlope = (this.midLoc.y - this.upperLoc.y) / (this.midLoc.x - this.upperLoc.x)
        this.lowerLineSlope = (this.midLoc.y - this.lowerLoc.y) / (this.midLoc.x - this.lowerLoc.x)

        this.smokingSmoke = this.game.add.sprite(PongPos.smokingX(), PongPos.smokingY(), PongAssets.SMOKING_CIG)
        this.smoke = this.smokingSmoke.animations.add("smoke")
        this.smoke.onComplete.add(() => {
            this.smokingSmoke.visible = false
        })
        this.smokingSmoke.visible = false
        Scale.scaleSprite(this.smokingSmoke, 0.5)

        this.vapingVape = this.game.add.sprite(PongPos.vapingX(), PongPos.vapingY(), PongAssets.VAPING_VAPE)
        this.vape = this.vapingVape.animations.add("vape")
        this.vape.onComplete.add(() => {
            this.vapingVape.visible = false
        })
        this.vapingVape.visible = false
        Scale.scaleSprite(this.vapingVape, 0.7)

        this.debugPanel = new DebugPanel(this.game)

        this.setupDifficultySelection()
        this.setupGame()
        this.startIntro()
    }

    update() {
        if (this.paddle && this.paddle.alive) {
            this.paddle.y = this.game.input.y
            if (this.paddle.y < 0) {
                this.paddle.y = 0
            } else if (this.paddle.y > Scale.height() - this.paddle.height) {
                this.paddle.y = Scale.height() - this.paddle.height
            }
        }
        if (this.undefendedCigs === PongState.MAX_UNDEFENDED_CIGS || this.timeLeft < 1) {
            this.gameOver()
        }
    }

    setupDifficultySelection = () => {
        this.diffGroup = this.game.add.group()
        this.addTitle("Addiction Pong", this.diffGroup)
        this.addInfo(
            "Use your mouse to move the paddle up and down and pong the cigarettes and e-cigarettes away from your mouth.",
            this.diffGroup)

        this.difficultyButtons = this.addButtons(PongPos.difficultyButtonY(), this.diffGroup,
            {
                name: "Easy", on: true, onClick: () => {
                    this.startGame(Difficulty.EASY)
                }
            },
            {
                name: "Medium", on: true, onClick: () => {
                    this.startGame(Difficulty.MEDIUM)
                }
            },
            {
                name: "Hard", on: true, onClick: () => {
                    this.startGame(Difficulty.HARD)
                }
            }
        )
        this.diffGroup.visible = false
    }

    /**
     * Called once to setup the game.
     */
    setupGame = () => {
        this.gameGroup = this.game.add.group()
        this.gameGroup.visible = false
        this.cigs = this.game.add.group()
        this.gameGroup.add(this.cigs)
        this.paddle = this.gameGroup.create(PongPos.paddleX(), PongPos.paddleY(), PongAssets.PADDLE)
        Scale.scaleSprite(this.paddle, 0.55)
        this.paddleBaseHeight = this.paddle.height
        this.game.physics.enable(this.paddle, Phaser.Physics.ARCADE)
        this.paddle.body.immovable = true

        this.difficultyText = PhaserText.create(this.game, "Not Set", {
            x: PongPos.timeRemainingX(), y: PongPos.difficultyTextY(), fontPx: 22, group: this.gameGroup,
            type: FontType.TITLE
        })
        PhaserText.create(this.game, "TIME REMAINING", {
            x: PongPos.timeRemainingX(), y: PongPos.timeRemainingY(), fontPx: 22, group: this.gameGroup,
            type: FontType.TITLE
        })
        this.timeRemainingCounter =
            PhaserText.create(this.game, "60", {
                x: PongPos.timeRemainingCounterX(), y: PongPos.timeRemainingCounterY(), fontPx: 22, group:
                this.gameGroup, type: FontType.TITLE
            })

        let makeMoreCigs = true
        let cigY = 0
        let heightToAdd = 0
        while (makeMoreCigs) {
            const cig = new Cig(this, cigY, this.cigs, cigY === 0)
            if (cigY === 0) {
                cigY = Math.ceil(cig.height)
                heightToAdd = Math.ceil(cigY * 1.5)
                cig.y = cigY
                cig.availablePosition = cigY
            }
            cigY += heightToAdd
            makeMoreCigs = !(cigY + heightToAdd > Scale.height())
        }
    }

    startIntro = () => {
        const title = this.addTitle("Addiction Pong")
        const info = this.addInfo(
            "Try your hand at Addiction Pong. " +
            "The goal of this game is for you to stay in control and keep " +
            "from smoking or vaping so that you won’t get addicted. Each " +
            "time you smoke or vape, the less control you have over " +
            "nicotine, and the more addicted you become. See if you can " +
            "keep from getting addicted by blocking the cigarettes and " +
            "e-cigarettes away from your mouth. You’ll have to play at " +
            "least 3 times before you can quit the game. Click on Easy, " +
            "Medium or Hard to start playing.")
        log.debug("games played: " + this.gamesPlayed)
        const intro = this.game.add.sound(PongAssets.INTRO)
        intro.onStop.addOnce(() => {
            title.destroy()
            info.destroy()
            this.startDifficultySelection()
        })
        intro.play()

        this.debugPanel.setSkip(() => {
            intro.stop()
        })
    }

    /**
     * Called every time to display the difficulty selection group.
     */
    startDifficultySelection = () => {
        this.diffGroup.visible = true
        if (this.gameOverDestroyGroup) {
            this.gameOverDestroyGroup.destroy()
        }
    }

    /**
     * Called every time when a new game starts.
     */
    startGame = (difficulty: Difficulty) => {
        this.diffGroup.visible = false
        this.gameGroup.visible = true

        this.currentDifficult = difficulty
        this.addictometer.animations.frame = 0

        let cigFlightDelay: number
        this.difficultyText.text = "DIFFICULTY: " + this.currentDifficult
        if (this.currentDifficult === Difficulty.EASY) {
            cigFlightDelay = 1000
            this.paddle.height = this.paddleBaseHeight
        } else if (this.currentDifficult === Difficulty.MEDIUM) {
            this.paddle.height = this.paddleBaseHeight * 0.6
            cigFlightDelay = 950
        } else if (this.currentDifficult === Difficulty.HARD) {
            this.paddle.height = this.paddleBaseHeight * 0.3
            cigFlightDelay = 900
        }

        this.gameTimer = this.game.time.create()
        this.cigLoop = this.gameTimer.loop(cigFlightDelay, () => {
            const cigsCopy = this.cigs.getAll().slice()
            CCUtils.shuffle(cigsCopy)
            for (const cig of cigsCopy) {
                if (cig.isReset) {
                    cig.makeFly()
                    break
                }
            }
        })
        this.timeRemainingCounter.text = this.timeLeft.toString()
        this.gameTimer.loop(1000, () => {
            this.timeLeft -= 1
            this.timeRemainingCounter.text = this.timeLeft.toString()
        })
        this.gameTimer.start()
    }

    /**
     * Called after every game. Shows the scores screen. (In explodOmeterAnim's callback.)
     */
    gameOver = () => {
        this.gamesPlayed++
        this.gameGroup.visible = false

        this.gameTimer.destroy()

        this.cigs.forEach((cig: Cig) => {
            cig.resetCig()
        }, this)

        this.gameOverDestroyGroup = this.game.add.group()
        this.gameOverDestroyGroup.visible = false

        let addictionText: string
        let addictionColor: string
        let audio: GameOverSounds
        let title = "Time's Up"
        if (this.undefendedCigs === 0) {
            addictionText = "NOT"
            addictionColor = COLOR.green
            audio = this.notAddictedSounds
        } else if (this.undefendedCigs < PongState.MAX_UNDEFENDED_CIGS) {
            addictionText = "SOMEWHAT"
            addictionColor = COLOR.orange
            audio = this.somewhatAddictedSounds
        } else {
            addictionText = "TOTALLY"
            addictionColor = COLOR.red
            title = "Game Over"
            audio = this.totallyAddictedSounds
        }

        this.addTitle(title, this.gameOverDestroyGroup)

        const youre = PhaserText.create(this.game, "You're", {
            x: PongPos.directionsX(), y: PongPos.directionsY(), group: this.gameOverDestroyGroup
        })
        youre.fontStyle = "bold"
        const addictionLevel = PhaserText.create(this.game, addictionText, {
            x: youre.x + youre.width + Scale.width() * 0.02, y: PongPos.directionsY(), fontPx: 36,
            group: this.gameOverDestroyGroup, color: addictionColor
        })
        addictionLevel.y += youre.height - addictionLevel.height
        const addicted = PhaserText.create(this.game, "addicted!", {
            x: addictionLevel.x + addictionLevel.width + Scale.width() * 0.02, y: PongPos.directionsY(),
            group: this.gameOverDestroyGroup
        })
        addicted.fontStyle = "bold"

        const undefendedCigs = this.undefendedCigs
        PongStore.saveUserScore(undefendedCigs)
        const resultsButtons = this.addButtons(PongPos.difficultyButtonY(), this.gameOverDestroyGroup,
            {
                name: "Play Again", on: false, onClick: () => {
                    this.startDifficultySelection()
                }
            },
            {
                name: "View Scores", on: false, onClick: () => {
                    this.displayScores(undefendedCigs)
                }
            },
            {
                name: "Done", on: false, onClick: () => {
                    this.backToTheArcade()
                }
            }
        )

        const toPlay = audio.sounds[audio.index]
        toPlay.onStop.addOnce(() => {
            this.enableButton(this.gamesPlayed < PongState.MAX_GAMES, resultsButtons[0])
            this.enableButton(true, resultsButtons[1])
            this.enableButton(this.gamesPlayed >= PongState.MIN_GAMES, resultsButtons[2])
            if (this.gamesPlayed < PongState.MIN_GAMES) {
                PhaserText.create(this.game, "You need to play again!", {x: Scale.x(0.45), y: Scale.y(0.7), group: this.gameOverDestroyGroup})
            }
        })

        if (audio.index < 2) {
            audio.index += 1
        }

        log.debug("game over undefended cigs: " + this.undefendedCigs)
        if (this.undefendedCigs >= PongState.MAX_UNDEFENDED_CIGS) {
            this.totallyAddicted.play()
            const flasher = this.game.time.create()
            flasher.add(500, () => {
                this.explodeOMeter.visible = true
            })
            flasher.add(1000, () => {
                this.explodeOMeter.visible = false
            })
            flasher.add(1500, () => {
                this.explodeOMeter.visible = true
            })
            flasher.add(2000, () => {
                this.explodeOMeter.visible = false
            })
            flasher.add(2500, () => {
                this.explodeOMeter.visible = true
                this.explodeOMeterAnim.onComplete.addOnce(() => {
                    this.explodeOMeter.visible = false
                    this.explodeOMeter.frame = 0
                    this.gameOverDestroyGroup.visible = true
                    toPlay.play()
                })
                log.debug("Starting explode of meter anim")
                this.explodeOMeterAnim.play(10)
                this.addictometer.frame = 11
            })
            flasher.start()
        } else {
            if (this.undefendedCigs > 0) {
                this.somewhatAddicted.play()
            } else {
                this.notAddicted.play()
            }

            this.gameOverDestroyGroup.visible = true
            toPlay.play()
        }

        // Reset these here so update doesn't continually cycle game over! Also so it is setup correctly if you resize
        // it mid-game.
        this.timeLeft = 60
        this.undefendedCigs = 0
    }

    displayScores = (undefendedCigs: number) => {
        this.scoresGroup = this.game.add.group()
        const scorePanel: Phaser.Sprite = this.scoresGroup.create(PongPos.scoresScreenX(), PongPos.scoresScreenY(),
            PongAssets.SCORE_SCREEN)
        Scale.scaleSpriteXY(scorePanel, 0.53, 0.5)

        const scores = PongStore.getScores()
        scores.unshift({screenname: "You are", score: undefendedCigs, date: new Date()})
        let currentScoreY = PongPos.scoresScreenY() + Scale.y(0.07)
        scores.forEach((score, index) => {
            if (index < 10) {
                let addictionText: string
                if (score.score === 0) {
                    addictionText = "not addicted"
                } else if (score.score < 10) {
                    addictionText = "somewhat addicted"
                } else {
                    addictionText = "totally addicted"
                }

                const username = PhaserText.create(this.game, score.screenname, {
                    x: PongPos.usernameX(), y: currentScoreY, fontPx: 14, type: FontType.MONOSPACE,
                    color: COLOR.neonGreen, group: this.scoresGroup
                })
                PhaserText.create(this.game, addictionText, {
                    x: PongPos.scoreX(), y: currentScoreY, fontPx: 14, type: FontType.MONOSPACE,
                    color: COLOR.neonGreen, group: this.scoresGroup
                })
                new Line(this.game, PongPos.usernameX(), username.y + username.height + Scale.y(0.005),
                    scorePanel.width * 0.83, username.y + username.height + Scale.y(0.005),
                    {group: this.scoresGroup, lineColor: colorNum.neonGreen}
                )
                currentScoreY += username.height * 1.7
            }
        })

        this.addButtons(PongPos.scoresScreenButtonsY(), this.scoresGroup,
            {
                name: "Play Again", on: this.gamesPlayed < PongState.MAX_GAMES, onClick: () => {
                    this.startDifficultySelection()
                    this.scoresGroup.destroy()
                }
            },
            {
                name: "Done", on: this.gamesPlayed >= PongState.MIN_GAMES, onClick: () => {
                    this.backToTheArcade()
                }
            }
        )
    }

    backToTheArcade = () => {
        if (this.scoresGroup) {
            this.scoresGroup.destroy()
        }
        if (this.gameOverDestroyGroup) {
            this.gameOverDestroyGroup.destroy()
        }



        this.addInfo(
            "Just like in this game, smoking and vaping get easier to " +
            "do and harder to stop with each cigarette or e-cigarette. " +
            "And before you know it, you’re addicted! Once nicotine " +
            "takes over, you’ve got to do what it wants, even when you " +
            "don’t want to. The best way to stay in control is to never " +
            "start smoking or vaping!"
        )
        const exitAudio = this.game.add.sound(PongAssets.BACK_TO_ARCADE)

        exitAudio.onStop.addOnce(() => {
            const totallyDone = this.game.time.create()

            totallyDone.add(1000, () => {
                this.game.state.start(TransitionState.ADDICTION_PONG_EXIT)
            })

            totallyDone.start()
        })

        exitAudio.play()
    }

    undefendedCig = (isCig: boolean) => {
        if (this.currentDifficult === Difficulty.EASY) {
            this.cigLoop.delay -= 25
        } else if (this.currentDifficult === Difficulty.MEDIUM) {
            this.cigLoop.delay -= 37
        } else if (this.currentDifficult === Difficulty.HARD) {
            this.cigLoop.delay -= 50
        }

        if (isCig) {
            this.vapingVape.visible = false
            this.smokingSmoke.visible = true
            this.smoke.play(10)
        } else {
            this.smokingSmoke.visible = false
            this.vapingVape.visible = true
            this.vape.play(10)
        }

        this.inhale.play()

        this.addictometer.animations.frame += 1
        this.undefendedCigs += 1
        log.debug("undefended cigs: " + this.undefendedCigs)

        if (this.undefendedCigs >= PongState.MAX_UNDEFENDED_CIGS) {
            this.gameOver()
        }
    }

    addTitle = (title: string, group?: Phaser.Group): Phaser.Text => {
        return PhaserText.create(this.game, title, {
            x: PongPos.titleX(), y: PongPos.titleY(), fontPx: 72,
            type: FontType.HEADER, group
        })
    }

    addInfo = (info: string, group?: Phaser.Group): Phaser.Text => {
        return PhaserText.create(this.game, info, {
            x: PongPos.directionsX(), y: PongPos.directionsY(), fontPx: 18, wordWrapWidth: 0.6,
            group
        })
    }

    /**
     * Works well for one, two, or three buttons. More might not fit.
     *
     * These buttons should just be recreated, because toggling them enabled / disabled would just be a pain.
     *
     * Button tuple is: [name, enabled, onClick]
     */
        // tslint:ignore-next-line
    addButtons = (
        buttonsY: number, buttonGroup: Phaser.Group,
        ...buttons: Array<{ name: string, on: boolean, onClick: () => void }>
    ): Array<ButtonContainer> => {

        let previousButton: Phaser.Button
        const theseButtons: Array<ButtonContainer> = []
        buttons.forEach((buttonInfo) => {
            let buttonX: number
            if (previousButton) {
                buttonX = previousButton.x + previousButton.width + PongPos.difficultyButtonSpacing()
            } else {
                buttonX = PongPos.difficultyButtonX()
            }

            const button = Buttons.create(this.game, buttonInfo.name, buttonInfo.onClick, {
                enabled: buttonInfo.on,
                group: buttonGroup,
                x: buttonX,
                y: buttonsY,
                buttonSize: ButtonSize.WIDE,
            })

            previousButton = button.button
            this.allTheButtons.push(button)
            theseButtons.push(button)
        })
        return theseButtons
    }

    enableButton = (enabled = true, button: ButtonContainer) => {
        button.enable(enabled)
    }

}

enum Difficulty {
    EASY = "EASY",
    MEDIUM = "MEDIUM",
    HARD = "HARD"
}

export class Loc {
    x: number
    y: number

    constructor(x: number, y: number) {
        this.x = x
        this.y = y
    }
}

class GameOverSounds {
    sounds: Array<Phaser.Sound> = []
    index = 0
}
